class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
  }

  open(triggeredBy) {
    if (this.classList.contains('is-empty') && !this.querySelector('.zshop-line')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    if (window.zodiacPosthog) window.zodiacPosthog.cartViewed();
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    const gen = this._openGen || 0;
    setTimeout(() => {
      if ((this._openGen || 0) !== gen) return;
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement =
          this.querySelector('.zshop-cart__panel') ||
          this.querySelector('.drawer__inner') ||
          this.querySelector('.zshop-cart__x');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this._openGen = (this._openGen || 0) + 1;
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.classList.remove('is-empty');
    this.productId = parsedState.id;

    const drawerHtml = parsedState.sections && parsedState.sections['cart-drawer'];
    if (drawerHtml) {
      const doc = new DOMParser().parseFromString(drawerHtml, 'text/html');
      const fresh = doc.querySelector('cart-drawer');
      if (fresh) {
        this.className = fresh.className;
        this.classList.add('animate', 'active');
        this.innerHTML = fresh.innerHTML;
      } else {
        const inner = doc.querySelector('#CartDrawer');
        const target = this.querySelector('#CartDrawer');
        if (inner && target) target.innerHTML = inner.innerHTML;
      }
    }

    const bubbleHtml = parsedState.sections && parsedState.sections['cart-icon-bubble'];
    const bubble = document.getElementById('cart-icon-bubble');
    if (bubbleHtml && bubble) {
      const el = new DOMParser().parseFromString(bubbleHtml, 'text/html').querySelector('.shopify-section');
      if (el) bubble.innerHTML = el.innerHTML;
    }

    setTimeout(() => {
      const overlay = this.querySelector('#CartDrawer-Overlay');
      if (overlay) overlay.addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const el = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return el ? el.innerHTML : '';
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  onCartUpdate() {}

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        section: 'cart-drawer',
        selector: 'cart-drawer',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value, 10);
    const index = event.target.dataset.index;
    const key = event.target.dataset.lineKey;
    let message = '';

    if (event.target.dataset.min && inputValue < event.target.dataset.min) {
      message = window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min);
    } else if (event.target.max && inputValue > parseInt(event.target.max, 10)) {
      message = window.quickOrderListStrings.max_error.replace('[max]', event.target.max);
    } else if (event.target.step && inputValue % parseInt(event.target.step, 10) !== 0) {
      message = window.quickOrderListStrings.step_error.replace('[step]', event.target.step);
    }

    if (message) {
      this.setValidity(event, index, message);
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.updateQuantity(
        index,
        inputValue,
        document.activeElement && document.activeElement.getAttribute('name'),
        event.target.dataset.quantityVariantId,
        key
      );
    }
  }

  updateQuantity(line, quantity, name, variantId, key) {
    this.enableLoading(line);
    const payload = {
      quantity: Number(quantity),
      sections: ['cart-drawer', 'cart-icon-bubble'],
      sections_url: '/',
    };
    if (key) payload.id = key;
    else payload.line = Number(line);
    if (Number(quantity) === 0 && window.zodiacPosthog) {
      const row =
        document.getElementById(`CartDrawer-Item-${line}`) ||
        this.querySelector(`[data-key="${key}"]`);
      window.zodiacPosthog.removedFromRow(row);
    }
    fetch('/cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify(payload),
    })
      .then((response) => response.json())
      .then((state) => {
        if (state.errors) {
          this.updateLiveRegions(line, state.errors);
          return;
        }
        const drawer = this.closest('cart-drawer') || document.querySelector('cart-drawer');
        if (drawer && typeof drawer.renderContents === 'function') {
          drawer.renderContents(state);
        }
        this.classList.toggle('is-empty', state.item_count === 0);
        if (drawer) {
          drawer.classList.toggle('is-empty', state.item_count === 0);
          if (state.item_count === 0 && typeof drawer.close === 'function') drawer.close();
        }
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-drawer', cartData: state, variantId: variantId });
      })
      .catch((e) => {
        console.error(e);
      })
      .finally(() => {
        this.disableLoading(line);
      });
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
